import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { useAuth } from "../../context/AuthContext";
import { hasActionAccess, hasWorkspaceAccess } from "../../lib/workspaces";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

interface ReferralRow {
  id: string;
  patient_contact_id: string;
  patient_name: string | null;
  patient_lifecycle_status: string | null;
  status: string;
  notes: string | null;
  remarks: string | null;
  created_at: string | null;
}
interface Contact360Data {
  contact: { id: string; first_name: string | null; last_name: string | null; lifecycle_status: string | null; email: string | null; phone: string | null; owner_id: string | null };
  referred_patients: ReferralRow[];
}
interface ContactDetail {
  id: string; first_name: string; last_name: string; email: string | null; phone: string | null;
  location: string | null; lifecycle_status: string; disposition: string | null; owner_id?: string | null; version: number;
  linked_organizations?: Array<{ organization_id: string; organization_name?: string | null; active?: boolean }>;
}
interface TenantUser { id: string; name: string; email: string; active: boolean }
interface RelationshipContact { id: string; full_name: string; role_type: string; email?: string | null; phone?: string | null; is_primary: boolean; active: boolean; end_date?: string | null }
interface ContactChoice { id: string; first_name: string; last_name: string; lifecycle_status: string; archived?: boolean }

const STATUSES = ["lead", "prospect", "contact", "patient", "inactive"];
const REFERRAL_STATUSES = ["referred", "contacted", "qualified", "converted", "closed_lost"];

export default function Contact360Panel({ contactId }: { contactId: string }) {
  const [data, setData] = useState<Contact360Data | null>(null);
  const [detail, setDetail] = useState<ContactDetail | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [contacts, setContacts] = useState<ContactChoice[]>([]);
  const [relationshipContacts, setRelationshipContacts] = useState<RelationshipContact[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", phone: "", location: "", lifecycle_status: "contact", disposition: "", owner_id: "" });
  const [referralMode, setReferralMode] = useState<"existing" | "new">("new");
  const [referralForm, setReferralForm] = useState({ organization_id: "", patient_contact_id: "", first_name: "", last_name: "", email: "", phone: "", location: "", status: "referred", notes: "", remarks: "" });
  const role = useEffectiveRole();
  const { user } = useAuth();
  const canView = canAtLeast(role, "viewer") && hasActionAccess(user, "contacts", "view");
  const canEdit = canAtLeast(role, "representative") && hasActionAccess(user, "contacts", "edit");
  const canAddReferral = canAtLeast(role, "manager_trainer") && hasActionAccess(user, "referrals", "add");
  const canReassign = role === "owner_admin" || role === "super_admin";
  const canViewRelationshipContacts = hasWorkspaceAccess(user, "relationship_contacts");

  useEffect(() => {
    const openEditor = () => canEdit && setEditOpen(true);
    window.addEventListener("rk:edit-contact", openEditor);
    return () => window.removeEventListener("rk:edit-contact", openEditor);
  }, [canEdit]);

  const load = useCallback(async () => {
    if (!contactId || !canView) return;
    try {
      const [{ data: view }, { data: contact }, { data: activePeople }, { data: inactivePeople }] = await Promise.all([
        api.get<Contact360Data>(`/crm/contacts/${contactId}/360`),
        api.get<ContactDetail>(`/crm/contacts/${contactId}`),
        canViewRelationshipContacts ? api.get<RelationshipContact[]>("/crm/relationship-contacts", { params: { contact_id: contactId, active: true } }) : Promise.resolve({ data: [] as RelationshipContact[] }),
        canViewRelationshipContacts ? api.get<RelationshipContact[]>("/crm/relationship-contacts", { params: { contact_id: contactId, active: false } }) : Promise.resolve({ data: [] as RelationshipContact[] }),
      ]);
      setData(view);
      setDetail(contact);
      setRelationshipContacts([...activePeople, ...inactivePeople]);
      setEditForm({
        first_name: contact.first_name || "", last_name: contact.last_name || "", email: contact.email || "",
        phone: contact.phone || "", location: contact.location || "", lifecycle_status: contact.lifecycle_status || "contact",
        disposition: contact.disposition || "", owner_id: contact.owner_id || view.contact.owner_id || "",
      });
    } catch (e) { toastError(e); }
  }, [contactId, canView, canViewRelationshipContacts]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canEdit) return;
    (async () => {
      try {
        const requests: Promise<any>[] = [];
        if (canReassign) requests.push(api.get("/tenant/users"));
        if (canAddReferral) requests.push(api.get("/crm/contacts", { params: { archived: false, limit: 500 } }));
        const results = await Promise.all(requests);
        let index = 0;
        if (canReassign) setUsers((results[index++]?.data || []).filter((u: TenantUser) => u.active));
        if (canAddReferral) setContacts((results[index]?.data || []).filter((c: ContactChoice) => c.id !== contactId && !c.archived));
      } catch (e) { toastError(e); }
    })();
  }, [canEdit, canReassign, canAddReferral, contactId]);

  const saveContact = async () => {
    if (!detail || !editForm.first_name.trim()) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        version: detail.version, first_name: editForm.first_name.trim(), last_name: editForm.last_name.trim(),
        email: editForm.email.trim() || null, phone: editForm.phone.trim() || null, location: editForm.location.trim() || null,
        lifecycle_status: editForm.lifecycle_status, disposition: editForm.disposition.trim() || null,
      };
      if (canReassign) payload.owner_id = editForm.owner_id || null;
      await api.patch(`/crm/contacts/${contactId}`, payload);
      toast.success("Contact updated");
      setEditOpen(false);
      await load();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const addReferral = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        referrer_contact_id: contactId,
        organization_id: referralForm.organization_id || null,
        status: referralForm.status,
        notes: referralForm.notes.trim() || null,
        remarks: referralForm.remarks.trim() || null,
      };
      if (referralMode === "new") {
        if (!referralForm.first_name.trim()) {
          toast.error("Patient first name is required.");
          return;
        }
        payload.new_patient = {
          first_name: referralForm.first_name.trim(),
          last_name: referralForm.last_name.trim(),
          email: referralForm.email.trim() || null,
          phone: referralForm.phone.trim() || null,
          location: referralForm.location.trim() || null,
          lifecycle_status: "patient",
          owner_id: detail?.owner_id || data?.contact.owner_id || null,
        };
      } else {
        if (!referralForm.patient_contact_id) {
          toast.error("Choose a patient to link.");
          return;
        }
        payload.patient_contact_id = referralForm.patient_contact_id;
      }
      await api.post(`/crm/contacts/${contactId}/referrals`, payload);
      toast.success(referralForm.organization_id ? "Referred patient added" : "Independent-practitioner referral added — no clinic linked");
      setReferralOpen(false);
      setReferralForm({ organization_id: "", patient_contact_id: "", first_name: "", last_name: "", email: "", phone: "", location: "", status: "referred", notes: "", remarks: "" });
      await load();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  if (!canView) return null;
  if (!data || !detail) return <div className="text-sm text-slate-500">Loading referral view…</div>;

  return (
    <section className="border border-slate-200 rounded-lg bg-white" data-testid="contact-360-panel">
      {canViewRelationshipContacts && <div className="border-b border-slate-200" data-testid="doctor-contact-persons">
        <header className="px-4 py-3 bg-slate-50 flex flex-wrap items-center justify-between gap-2"><div><h2 className="text-sm font-semibold text-slate-800">Doctor contact persons</h2><p className="text-xs text-slate-500">Assistants and staff linked specifically to this doctor.</p></div><Button asChild size="sm" variant="outline"><Link to={`/relationship-contacts?contact_id=${contactId}`}>Manage contact persons</Link></Button></header>
        {relationshipContacts.length === 0 ? <p className="px-4 py-4 text-sm text-slate-500">No doctor contact persons linked yet.</p> : <div className="divide-y divide-slate-100">{relationshipContacts.map((person) => <div key={person.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3"><div><div className="font-medium text-sm">{person.full_name}{person.is_primary ? " · Primary" : ""}</div><div className="text-xs text-slate-500">{person.role_type}</div></div><div className="text-sm text-slate-600 text-right"><div>{person.email || "—"}</div><div>{person.phone || "—"}</div><div className={person.active ? "text-xs text-emerald-700" : "text-xs text-slate-500"}>{person.active ? "Active" : `Inactive${person.end_date ? ` · ended ${person.end_date}` : ""}`}</div></div></div>)}</div>}
      </div>}
      <header className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-800">Referral 360</h2>
        <div className="flex gap-2">
          {canAddReferral && <Button size="sm" onClick={() => setReferralOpen(true)} data-testid="add-referral-btn"><Plus className="w-4 h-4 mr-1" /> Add referred patient</Button>}
        </div>
      </header>
      <div className="px-4 py-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {data.referred_patients.length === 0
            ? "No referred patients yet."
            : `${data.referred_patients.length} referred patient${data.referred_patients.length === 1 ? "" : "s"} linked to this contact.`}
        </p>
        {canAddReferral && <Button asChild size="sm" variant="outline"><Link to="/referrals">Open Referrals workspace</Link></Button>}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-contact-dialog">
          <DialogHeader><DialogTitle>Edit contact</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First name</Label><Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} /></div>
            <div><Label>Last name</Label><Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address / territory</Label><Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} /></div>
            <div><Label>Lifecycle</Label><Select value={editForm.lifecycle_status} onValueChange={(v) => setEditForm({ ...editForm, lifecycle_status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Disposition</Label><Input value={editForm.disposition} onChange={(e) => setEditForm({ ...editForm, disposition: e.target.value })} /></div>
            {canReassign && <div className="col-span-2"><Label>Assigned owner / staff</Label><Select value={editForm.owner_id || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, owner_id: v === "__none__" ? "" : v })}><SelectTrigger data-testid="contact-owner-select"><SelectValue placeholder="Unassigned" /></SelectTrigger><SelectContent><SelectItem value="__none__">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent></Select><p className="mt-1 text-xs text-slate-500">Reassignment changes future responsibility only. Existing commission history is preserved.</p></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button><Button onClick={saveContact} disabled={saving || !editForm.first_name.trim()} data-testid="save-contact-btn">{saving ? "Saving…" : "Save changes"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={referralOpen} onOpenChange={setReferralOpen}>
        <DialogContent data-testid="add-referral-dialog">
          <DialogHeader><DialogTitle>Add referred patient</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Patient record</Label><Select value={referralMode} onValueChange={(v) => setReferralMode(v as "existing" | "new")}><SelectTrigger data-testid="referral-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new">Create new patient</SelectItem><SelectItem value="existing">Link existing patient</SelectItem></SelectContent></Select></div>
            {referralMode === "existing" ? <div><Label>Existing patient</Label><Select value={referralForm.patient_contact_id} onValueChange={(v) => setReferralForm({ ...referralForm, patient_contact_id: v })}><SelectTrigger data-testid="existing-patient-select"><SelectValue placeholder="Choose patient" /></SelectTrigger><SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name || ""} · {c.lifecycle_status}</SelectItem>)}</SelectContent></Select></div> : <div className="grid grid-cols-2 gap-3">
              <div><Label>First name</Label><Input value={referralForm.first_name} onChange={(e) => setReferralForm({ ...referralForm, first_name: e.target.value })} /></div>
              <div><Label>Last name</Label><Input value={referralForm.last_name} onChange={(e) => setReferralForm({ ...referralForm, last_name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={referralForm.email} onChange={(e) => setReferralForm({ ...referralForm, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={referralForm.phone} onChange={(e) => setReferralForm({ ...referralForm, phone: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address / territory</Label><Input value={referralForm.location} onChange={(e) => setReferralForm({ ...referralForm, location: e.target.value })} /></div>
            </div>}
            <div className="col-span-2"><Label>Clinic / hospital (recommended)</Label><Select value={referralForm.organization_id || "__independent__"} onValueChange={(v) => setReferralForm({ ...referralForm, organization_id: v === "__independent__" ? "" : v })}><SelectTrigger data-testid="contact360-referral-organization"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__independent__">Independent practitioner — no clinic</SelectItem>{(detail.linked_organizations || []).filter((row) => row.active !== false).map((row) => <SelectItem key={row.organization_id} value={row.organization_id}>{row.organization_name || "Unnamed organization"}</SelectItem>)}</SelectContent></Select><p className="mt-1 text-xs text-amber-700">Clinic attribution improves territory and referral-source reporting, but it does not block independent practitioners.</p></div>
            <div><Label>Referral status</Label><Select value={referralForm.status} onValueChange={(v) => setReferralForm({ ...referralForm, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{REFERRAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Notes</Label><Input value={referralForm.notes} onChange={(e) => setReferralForm({ ...referralForm, notes: e.target.value })} /></div>
            <div><Label>Remarks</Label><Input value={referralForm.remarks} onChange={(e) => setReferralForm({ ...referralForm, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setReferralOpen(false)}>Cancel</Button><Button onClick={addReferral} disabled={saving || (referralMode === "new" ? !referralForm.first_name.trim() : !referralForm.patient_contact_id)} data-testid="save-referral-btn">{saving ? "Saving…" : "Add patient"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
